import React from 'react';
import styled, { keyframes } from 'styled-components';

interface WarningNoticeCardProps {
  title: string;
  content: string;
  timestamp?: string;
}

const WarningNoticeCard: React.FC<WarningNoticeCardProps> = ({ title, content, timestamp = 'just now' }) => {
  return (
    <StyledWrapper>
      <div className="card">
        <div className="img warning-icon" />
        <div className="textBox">
          <div className="textContent">
            <p className="h1">{title}</p>
            <span className="span">{timestamp}</span>
          </div>
          <p className="p">{content}</p>
        </div>
      </div>
    </StyledWrapper>
  );
};

const swingIn = keyframes`
  0% {
    transform: translateX(-100%) rotate(-15deg);
    opacity: 0;
  }
  20% {
    transform: translateX(0) rotate(10deg);
    opacity: 0.7;
  }
  40% {
    transform: translateX(0) rotate(-8deg);
  }
  60% {
    transform: translateX(0) rotate(6deg);
  }
  80% {
    transform: translateX(0) rotate(-3deg);
  }
  100% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(255, 193, 7, 0.5), 0 0 30px rgba(255, 193, 7, 0.2);
  }
`;

const StyledWrapper = styled.div`
  .card {
    width: 100%;
    height: 80px;
    background: linear-gradient(135deg, #FFC107, #FF8F00);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: left;
    backdrop-filter: blur(10px);
    transition: 0.3s ease-in-out;
    animation: ${swingIn} 0.7s ease-out, ${glow} 3s infinite;
    box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
  }

  .card:hover {
    cursor: pointer;
    transform: translateY(-2px) scale(1.02);
    animation-play-state: paused;
    box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
  }

  .img {
    width: 50px;
    height: 50px;
    margin-left: 15px;
    border-radius: 12px;
    transition: 0.3s ease-in-out;
  }

  .warning-icon {
    background: linear-gradient(135deg, #FFCA28, #FFC107);
    position: relative;
  }

  .warning-icon::after {
    content: '⚡';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
  }

  .textBox {
    width: calc(100% - 90px);
    margin-left: 15px;
    color: #333;
    font-family: 'Poppins', sans-serif;
  }

  .textContent {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .span {
    font-size: 11px;
    opacity: 0.7;
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;
    color: #666;
  }

  .h1 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: #333;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
  }

  .p {
    font-size: 13px;
    font-weight: 400;
    margin: 0;
    opacity: 0.8;
    line-height: 1.3;
    color: #444;
  }
`;

export default WarningNoticeCard;